import { Card, CardContent } from '@app/ui';
import { PageTransition, StaggerContainer, StaggerItem } from '@app/ui/motion';
import { formatCurrency } from '@app/validators';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { RouteErrorComponent } from '../../components/route-error';
import { api } from '../../lib/api';

export const Route = createFileRoute('/admin/')({
  loader: async ({ context }) => {
    const { getServerApi } = await import('../../lib/api-server');
    const serverApi = await getServerApi();
    await context.queryClient.ensureQueryData(serverApi.admin.dashboard.queryOptions());
  },
  pendingComponent: () => (
    <div className="animate-pulse">
      <div className="h-8 bg-muted rounded w-48 mb-6" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
          <div key={i} className="h-20 bg-muted rounded" />
        ))}
      </div>
    </div>
  ),
  errorComponent: ({ error }) => <RouteErrorComponent error={error} />,
  component: Dashboard,
});

function Dashboard() {
  const { data } = useSuspenseQuery(api.admin.dashboard.queryOptions());

  return (
    <PageTransition>
      <h1 className="text-2xl font-bold">Dashboard</h1>
      <p className="mt-2 text-muted-foreground">Platform metrics and overview.</p>

      <StaggerContainer className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StaggerItem>
          <MetricCard title="Total Athletes" value={String(data.totalAthletes)} />
        </StaggerItem>
        <StaggerItem>
          <MetricCard title="Active Deals" value={String(data.activeDeals)} />
        </StaggerItem>
        <StaggerItem>
          <MetricCard title="Universities" value={String(data.universities)} />
        </StaggerItem>
        <StaggerItem>
          <MetricCard title="Revenue (MRR)" value={formatCurrency(data.mrr)} />
        </StaggerItem>
      </StaggerContainer>

      <StaggerContainer
        delay={0.2}
        className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
      >
        <StaggerItem>
          <MetricCard title="Total Deal Volume" value={formatCurrency(data.dealVolume)} />
        </StaggerItem>
        <StaggerItem>
          <MetricCard title="Avg Deal Size" value={formatCurrency(data.avgDealSize)} />
        </StaggerItem>
        <StaggerItem>
          <MetricCard title="AI Extractions" value={String(data.aiExtractions)} />
        </StaggerItem>
        <StaggerItem>
          <MetricCard title="Pending Payments" value={formatCurrency(data.pendingPayments)} />
        </StaggerItem>
      </StaggerContainer>
    </PageTransition>
  );
}

function MetricCard({ title, value }: { title: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-sm text-muted-foreground">{title}</p>
        <p className="mt-1 text-2xl font-semibold">{value}</p>
      </CardContent>
    </Card>
  );
}
